import React, { useEffect, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { SnackbarProvider } from 'notistack';
import Notifier from 'containers/Notifier';
import Routes from './Routes';
import { fetchAuthenticatedUser, logout } from 'store/auth/actions';
import {
  makeSelectToken,
  makeSelectUser,
  userJwtDataSelector,
} from 'store/auth/selectors';
import AppBar from 'components/AppBar';
import styles from '../App/App.module.scss';
import SideBar from '../../components/SideBar';
import useWindowDimensions from '../../utils/dimensionHook';
import { useTranslation } from 'react-i18next';
import SnackbarComponent from '../../components/Snackbar/SnackbarComponent';
import { useHistory } from 'react-router-dom';
import { LOGIN, MAINTENANCE } from '../../routes';
import Loader from '../Loader';
import adminService from '../../services/AdminService';

function App() {
  const dispatch = useDispatch();
  const { i18n } = useTranslation();
  const token = useSelector(makeSelectToken());
  const user = useSelector(makeSelectUser());
  const userJwtData = useSelector(userJwtDataSelector());
  const handleLogout = useCallback(() => dispatch(logout()), [dispatch]);
  const isMaintenanceMode = process.env.REACT_APP_MAINTENANCE_MODE === 'true';

  const history = useHistory();

  const handleLanguageChange = useCallback(
    async (val) => {
      try {
        const data = {
          selected_language: val,
        };

        await adminService.editAdminLanguage(data, user.id);
        dispatch(fetchAuthenticatedUser());
        i18n.changeLanguage(val);
      } catch (error) {
        console.error({ error });
      }
    },
    [i18n, user]
  );

  const [isHidden, setIsHidden] = useState(false);
  const { width } = useWindowDimensions();

  useEffect(() => {
    if (width < 1200) {
      setIsHidden(true);
    } else {
      setIsHidden(false);
    }
  }, [width]);
  useEffect(() => {
    if (isMaintenanceMode) {
      history.push(MAINTENANCE);
    } else {
      if (token) {
        dispatch(fetchAuthenticatedUser());
      } else {
        history.push(LOGIN);
      }
    }
  }, [token, dispatch]);

  const renderLoadingIndicator = <div>Loading...</div>;

  const onPressHidden = (value) => {
    setIsHidden(value);
  };

  return (
    <HelmetProvider>
      <SnackbarProvider>
        <Helmet>
          <title>Lumanity Admin</title>
        </Helmet>
        {token && !user ? (
          renderLoadingIndicator
        ) : (
          <>
            <div style={{ display: 'flex' }}>
              <Loader />
              {user && (
                <div style={{ width: isHidden ? '80px' : '250px' }}>
                  <SideBar
                    onSideBarOpen={(value) => onPressHidden(value)}
                    user={user}
                  />
                </div>
              )}
              <div className={styles.container}>
                <div
                  className={styles.appBarDiv}
                  style={{
                    left: isHidden ? 80 : 250,
                    width: isHidden ? width - 80 : width - 250,
                  }}
                >
                  {user && (
                    <AppBar
                      onLanguageChange={handleLanguageChange}
                      user={user}
                      onLogout={handleLogout}
                      isHidden={isHidden}
                    />
                  )}
                </div>
                <div
                  className={styles.contentDiv}
                  style={{
                    left: user ? (isHidden ? 80 : 250) : 0,
                    top: user ? 80 : 0,
                    width: user ? (isHidden ? width - 80 : width - 250) : width,
                  }}
                >
                  <Routes user={userJwtData} />
                </div>
              </div>
            </div>
          </>
        )}
        <SnackbarComponent />
        <Notifier />
      </SnackbarProvider>
    </HelmetProvider>
  );
}

export default App;
